// Utils
import cn from '../../../../utils/cn';

// Hooks
import useColorField from './useColorField';

// Components
import { PopoverContent, PopoverTrigger } from '../../../atoms/Popover/Popover';
import { Popover } from '../../../../store/popover/PopoverContext';

// Styles
import styles from './ColorField.module.css';

/**
 * ColorField - Custom color picker component
 * @param {object} props
 * @param {string} [props.value] - Current color value in HEX format
 * @param {(value: string) => void} [props.onChange] - Change handler
 * @param {string} [props.name] - Input name attribute
 * @param {boolean} [props.disabled] - Whether input is disabled
 * @param {boolean} [props.readOnly] - Read-only (posted, not user-editable)
 * @param {boolean} [props.required] - Whether input is required
 * @param {string} [props.error] - Validation error
 * @param {string} [props.className] - Additional CSS classes
 * @param {'default' | 'light' | 'table'} [props.theme] - Theme of the color picker
 * @param {string} [props.placeholder] - Placeholder text
 * @param {(e: React.FocusEvent<HTMLInputElement>) => void} [props.onBlur] - Blur handler
 * @param {(e: React.KeyboardEvent<HTMLInputElement>) => void} [props.onKeyDown] - Key down handler
 * @param {boolean} [props.initialOpen] - Whether to open popover initially (for direct edit)
 * @returns {JSX.Element} ColorField component
 */
function ColorField({
  value = '',
  onChange,
  name,
  disabled = false,
  readOnly = false,
  required = false,
  error = '',
  className = '',
  theme = 'light',
  placeholder = '',
  onBlur,
  onKeyDown,
  initialOpen = false,
}) {
  const {
    isOpen,
    setIsOpen,
    isEmpty,
    inputRef,
    DEFAULT_COLORS,
    DEFAULT_GREYS,
    handleColorSelect,
    handleInputClick,
    handleInputChange,
    handleInputBlur,
    getInputStyle,
  } = useColorField({
    value,
    onChange,
    disabled: disabled || readOnly,
    initialOpen,
  });

  return (
    <div className={cn([styles.wrapper, styles[theme], error && styles['has-error'], className])}>
      <Popover
        open={isOpen}
        onOpenChange={setIsOpen}
        placement="top"
      >
        <PopoverTrigger asChild>
          <input
            autoComplete="off"
            ref={inputRef}
            type="text"
            name={name}
            value={value}
            onChange={handleInputChange}
            onBlur={(e) => {
              handleInputBlur(e);
              onBlur?.(e);
            }}
            onKeyDown={onKeyDown}
            onClick={handleInputClick}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            placeholder={placeholder}
            className={cn([
              styles.input,
              styles[theme],
              isEmpty && styles['input-empty'],
              disabled && styles['input-disabled'],
              readOnly && styles['input-read-only'],
              error && styles['input-error'],
            ])}
            style={getInputStyle()}
          />
        </PopoverTrigger>
        <PopoverContent>
          <div className={styles['color-picker']}>
            <div className={styles['colors-grid']}>
              <div className={styles['colors-main']}>
                {DEFAULT_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={cn([
                      styles['color-item'],
                      value === color && styles['color-item-selected'],
                    ])}
                    style={{ backgroundColor: color }}
                    onClick={() => handleColorSelect(color)}
                    title={color}
                    aria-label={`Sélectionner la couleur ${color}`}
                  />
                ))}
              </div>
              <div className={styles['colors-greys']}>
                {DEFAULT_GREYS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={cn([
                      styles['color-item'],
                      value === color && styles['color-item-selected'],
                    ])}
                    style={{ backgroundColor: color }}
                    onClick={() => handleColorSelect(color)}
                    title={color}
                    aria-label={`Sélectionner la couleur ${color}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <div className={styles['color-square']} style={{ backgroundColor: value }} />

      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

export default ColorField;
